import React, { useEffect, useState, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import LandingPage from "./components/landing/LandingPage";
import CookieBanner from "./components/landing/CookieBanner";
import InstallPrompt from "./components/pwa/InstallPrompt";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useTier } from "./hooks/useTier";
import UpsellProvider, { useUpsell } from "./components/paywall/UpsellProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";

const Sidebar = lazy(() => import("./components/layout/Sidebar"));
const BottomNav = lazy(() => import("./components/layout/BottomNav"));
const Header = lazy(() => import("./components/layout/Header"));
const FloatingAIButton = lazy(() => import("./components/ai/AIButton"));
const JournalPage = lazy(() => import("./components/pages/JournalPage"));
const CalendarPage = lazy(() => import("./components/pages/CalendarPage"));
const GoalPage = lazy(() => import("./components/pages/GoalPage"));
const AccountSettings = lazy(() => import("./components/pages/AccountSettings"));
const AIChat = lazy(() => import("./components/ai/AIChat"));
const AuthDialog = lazy(() => import("./components/pages/AuthDialog"));
const ImpressumPage = lazy(() => import("./components/legal/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./components/legal/DatenschutzPage"));
const AGBPage = lazy(() => import("./components/legal/AGBPage"));

// AnalyticsDashboard pulls in recharts. Lazy-load it so the recharts bundle
// is split out of the initial main chunk - this keeps the public landing
// page fast on first paint.
const AnalyticsDashboard = lazy(() =>
  import("./components/analytics/AnalyticsDashboard")
);
// AdminDashboard lazy-loaded to keep admin code (UserManagement etc.) out of
// the public main chunk — same pattern as AnalyticsDashboard above.
const AdminDashboard = lazy(() =>
  import("./components/pages/AdminDashboard")
);

function RouteFallback() {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen flex items-center justify-center bg-white text-gray-400">
      {t("common.loading")}
    </div>
  );
}

const queryClient = new QueryClient();
const DEMO_STARTED_AT_KEY = "reflecta.demo.startedAt";
const DEMO_DURATION_MS = 5 * 60 * 1000;

const isDemoMode = () => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem("demoMode") === "1";
};

function DemoBanner() {
  const { t } = useLanguage();
  const [remainingMs, setRemainingMs] = useState(DEMO_DURATION_MS);

  useEffect(() => {
    let startedAt = Number(window.localStorage.getItem(DEMO_STARTED_AT_KEY));
    if (!startedAt) {
      startedAt = Date.now();
      window.localStorage.setItem(DEMO_STARTED_AT_KEY, String(startedAt));
    }

    const updateRemaining = () => {
      setRemainingMs(Math.max(0, DEMO_DURATION_MS - (Date.now() - startedAt)));
    };

    updateRemaining();
    const interval = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(interval);
  }, []);

  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);

  const handleSignup = () => {
    window.localStorage.removeItem("demoMode");
    window.localStorage.removeItem(DEMO_STARTED_AT_KEY);
    window.location.href = "/auth?mode=signup";
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b border-purple-100 bg-purple-50 px-4 md:px-8 py-3 text-sm">
      <div className="min-w-0">
        <span className="font-semibold text-[#6A0DAD]">{t("demo.mode")}</span>
        <span className="ml-2 text-gray-600">
          {minutes}:{String(seconds).padStart(2, "0")} {t("demo.remaining")}
        </span>
      </div>
      <button
        type="button"
        onClick={handleSignup}
        className="rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:shadow-md flex-shrink-0"
      >
        {t("demo.createAccount")}
      </button>
    </div>
  );
}

function AuthenticatedApp() {
  const [activeTab, setActiveTab] = useState("journal");
  const [showAIChat, setShowAIChat] = useState(false);
  const location = useLocation();
  const { isPremium } = useTier();
  const upsell = useUpsell();
  const { t } = useLanguage();

  // Minimal sub-routing inside the authenticated app: pick the active tab from
  // the URL when present (e.g. /app/billing/checkout for the post-signup
  // premium handoff). The existing tab UI is otherwise unchanged.
  const path = location.pathname;
  const isCheckout = path.startsWith("/app/billing/checkout");

  const handleChatClick = () => {
    if (!isPremium) {
      upsell?.open(t("upsell.chatReason"));
      return;
    }
    setShowAIChat((value) => !value);
  };

  return (
    <div className="flex h-screen bg-white text-gray-900">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header activeTab={activeTab} />
        {isDemoMode() && <DemoBanner />}

        <main className="flex-1 flex overflow-hidden">
          <div
            className={`flex-1 overflow-y-auto p-4 md:p-8 pb-20 md:pb-8 ${
              showAIChat ? "md:pr-4" : ""
            }`}
          >
            {isCheckout ? (
              <div className="mx-auto flex min-h-full max-w-2xl items-center justify-center py-12">
                <section className="w-full rounded-2xl border border-gray-100 bg-gradient-to-br from-purple-50 via-white to-white p-8 text-center shadow-2xl">
                  <span className="inline-flex rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    {t("common.premium")}
                  </span>
                  <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-gray-900">
                    {t("checkout.title")}
                  </h1>
                  <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-gray-600">
                    {t("checkout.description")}
                  </p>
                  <button
                    type="button"
                    onClick={() => upsell?.open(t("common.premium"))}
                    className="mt-7 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
                  >
                    {t("common.goPremium")}
                  </button>
                </section>
              </div>
            ) : (
              <>
                {activeTab === "journal" && <JournalPage />}
                {activeTab === "goals" && <GoalPage />}
                {activeTab === "calendar" && <CalendarPage />}
                {activeTab === "analytics" && (
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center py-20 text-gray-400">
                        {t("analytics.loading")}
                      </div>
                    }
                  >
                    <AnalyticsDashboard />
                  </Suspense>
                )}
                {activeTab === "settings" && <AccountSettings />}
                {activeTab === "admin" && (
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center py-20 text-gray-400">
                        {t("common.loading")}
                      </div>
                    }
                  >
                    <AdminDashboard />
                  </Suspense>
                )}
              </>
            )}
          </div>

          {showAIChat && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
              <div className="relative w-full max-w-lg">
                <AIChat onClose={() => setShowAIChat(false)} />
              </div>
            </div>
          )}
        </main>

        <div className="fixed bottom-20 right-6 z-40 md:bottom-6">
          <FloatingAIButton onClick={handleChatClick} />
        </div>

        <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </div>
  );
}

// Guard for /app/* routes - bounces unauthenticated users to /auth.
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">{t("common.loading")}</p>
      </div>
    );
  }
  if (!user) {
    const search = location.pathname.startsWith("/app/billing/checkout")
      ? "?mode=signup"
      : "?mode=login";
    return <Navigate to={`/auth${search}`} replace />;
  }
  return children;
}

// Bounce already-authenticated users away from / and /auth into the app.
function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route
            path="/"
            element={
              <PublicOnly>
                <LandingPage />
              </PublicOnly>
            }
          />
          <Route
            path="/auth"
            element={
              <PublicOnly>
                <AuthDialog />
              </PublicOnly>
            }
          />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/agb" element={<AGBPage />} />
          <Route
            path="/app/*"
            element={
              <RequireAuth>
                <AuthenticatedApp />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <CookieBanner />
      <InstallPrompt />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <LanguageProvider>
            <UpsellProvider>
              <AppRoutes />
            </UpsellProvider>
          </LanguageProvider>
        </QueryClientProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
